import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";

import {
	Breadcrumbs,
	Button,
	MainContainer,
	UnderwritingBreadcrumbs,
} from "components";
import { LineDivider } from "components/layout";
import { breadcrumbsReportFromMerchantPart } from "helpers";
import { getResultTableData, LABELS_TIN_CHECK_RESULT } from "mappers";
import {
	useDownloadTINCheckDocumentMutation,
	useTINCheckAuroraOutcomeFieldMutation,
	useTINCHeckResultQuery,
} from "queries";
import {
	getCurrentUserAccessToken,
	getCurrentUserId,
} from "store/user/user.reducer";
import { UnderwritingTabs, UnderwritingRouteMatchProps } from "types";

import ReportTable from "./reportTable";

const MerchantTINCheckReport = () => {
	const currentUserId = useSelector(getCurrentUserId);
	const token: string = useSelector(getCurrentUserAccessToken);
	const match: UnderwritingRouteMatchProps = useRouteMatch();
	const isMerchantPage = useMemo(
		() => match?.url?.startsWith("/merchant/"),
		[match?.url],
	);

	const applicationId = useMemo(
		() =>
			isMerchantPage ? match?.params?.applicationId : match?.params?.id,
		[isMerchantPage, match?.params?.applicationId, match?.params?.id],
	);

	const { data, isFetching, refetch } = useTINCHeckResultQuery({
		id: applicationId,
	});

	const {
		mutate: downloadTINCheckDocument,
		isLoading: isLoadingDownloadTINCheckDocument,
	} = useDownloadTINCheckDocumentMutation();

	const { mutate: submitAuroraOutcome, isLoading: isLoadingAuroraOutcome } =
		useTINCheckAuroraOutcomeFieldMutation();

	const submitAuroraOutcomeHandler = useCallback(
		values =>
			submitAuroraOutcome({
				id: applicationId,
				values,
				onSuccessHandler: () => refetch(),
			}),
		[applicationId, refetch, submitAuroraOutcome],
	);

	const downloadDocument = () =>
		downloadTINCheckDocument({
			id: applicationId,
			token: token,
		});

	const tinCheckResult = getResultTableData(
		data?.original_inquiry,
		data?.re_run_inquiry,
		LABELS_TIN_CHECK_RESULT,
	);

	const isRerun = useMemo(
		() => !!data?.re_run_inquiry,
		[data?.re_run_inquiry],
	);

	const editableFields = useMemo(() => {
		let fields = {};
		if (
			!isMerchantPage &&
			match?.params?.tab !== UnderwritingTabs.Unassigned.name &&
			match?.params?.tab !== UnderwritingTabs.Historical.name &&
			match?.params?.tab !== UnderwritingTabs.QAUnassigned.name &&
			match?.params?.tab !== UnderwritingTabs.QAComplete.name &&
			data?.underwriter_id &&
			data?.underwriter_id === currentUserId
		) {
			fields = {
				...fields,
				aurora_outcome: {
					submitHandler: submitAuroraOutcomeHandler,
					isLoading: isLoadingAuroraOutcome,
				},
			};
		}
		return fields;
	}, [
		currentUserId,
		data?.underwriter_id,
		isLoadingAuroraOutcome,
		isMerchantPage,
		match?.params?.tab,
		submitAuroraOutcomeHandler,
	]);

	return (
		<MainContainer>
			{isMerchantPage ? (
				<Breadcrumbs
					pages={breadcrumbsReportFromMerchantPart({
						id: match.params.id,
						title: "TIN check Result",
					})}
				/>
			) : (
				<UnderwritingBreadcrumbs match={match} />
			)}
			<div className={isFetching ? "mt-4" : "mt-0"}>
				<ReportTable
					data={tinCheckResult}
					isRerun={isRerun}
					isLoading={isFetching}
					editableFields={editableFields}
				/>
			</div>
			<LineDivider />
			<Button
				type="button"
				value="Download"
				disabled={isLoadingDownloadTINCheckDocument}
				onClick={downloadDocument}
				isLoading={isFetching || isLoadingDownloadTINCheckDocument}
			/>
		</MainContainer>
	);
};

export default MerchantTINCheckReport;
